/**
 * Dashboard utility functions for calculating and formatting system-wide metrics
 * from track performance data.
 */

/**
 * Calculate overall system performance metrics from track performance data
 * @param {Object} trackPerformanceData - Track performance data from API
 * @param {Object} historicalData - Historical results data (optional)
 * @returns {Object} System-wide performance metrics
 */
export const calculateSystemMetrics = (trackPerformanceData, historicalData = null) => {
  // Initialize default metrics
  const defaultMetrics = {
    totalROI: 0,
    totalWinRate: 0,
    totalWins: 0,
    totalPlaces: 0,
    totalRaces: 0,
    totalTips: 0,
    netReturns: 0,
    averageROI: 0,
    totalBets: 0,
    totalReturns: 0,
    lastUpdated: new Date()
  };

  // Return defaults if no data
  if (!trackPerformanceData?.data || !Array.isArray(trackPerformanceData.data)) {
    return defaultMetrics;
  }

  let systemTotals = {
    totalBets: 0,
    totalReturns: 0,
    totalWins: 0,
    totalPlaces: 0,
    totalRaces: 0,
    trackCount: 0
  };

  // Process each track's data
  trackPerformanceData.data.forEach(track => {
    // Skip if no models data
    if (!track.models?.HayiAuto) return;

    const trackData = track.models.HayiAuto;
    systemTotals.trackCount++;

    // Add total races
    systemTotals.totalRaces += trackData.totalRaces || 0;

    // Process early and raceTime data
    ['early', 'raceTime'].forEach(timeType => {
      const timeData = trackData[timeType];
      if (!timeData) return;

      // Add bets and returns
      systemTotals.totalBets += timeData.totalBets || 0;
      systemTotals.totalReturns += timeData.totalReturns || 0;

      // Process positions data
      if (timeData.positions) {
        const positions = timeData.positions;

        // Total wins = sum of wins across all available positions (p1..pN)
        Object.values(positions).forEach((position) => {
          systemTotals.totalWins += position?.wins || 0;
        });

        // Places = p2 + p3 wins (when present)
        systemTotals.totalPlaces += (positions.p2?.wins || 0) + (positions.p3?.wins || 0);
      }
    });
  });

  // Calculate final metrics
  const totalTips = systemTotals.totalBets;
  const netReturns = systemTotals.totalReturns - systemTotals.totalBets;
  const totalWinRate = totalTips > 0 ? (systemTotals.totalWins / totalTips) * 100 : 0;
  const totalROI = totalTips > 0 ? (netReturns / totalTips) * 100 : 0;
  const averageROI = totalROI;

  return {
    totalROI,
    totalWinRate,
    totalWins: systemTotals.totalWins,
    totalPlaces: systemTotals.totalPlaces,
    totalRaces: systemTotals.totalRaces,
    totalTips,
    netReturns,
    averageROI,
    totalBets: systemTotals.totalBets,
    totalReturns: systemTotals.totalReturns,
    lastUpdated: new Date()
  };
};

/**
 * Format metric values for display based on type
 * @param {number|string} value - The value to format
 * @param {string} type - The type of metric ('percentage', 'currency', 'number', 'roi')
 * @returns {string} Formatted value string
 */
export const formatMetricValue = (value, type) => {
  if (value === null || value === undefined) {
    return '0';
  }

  switch (type) {
    case 'percentage':
    case 'roi':
      {
        const numValue = Number(value);
        if (!Number.isFinite(numValue)) return '0';
        return `${numValue.toFixed(1)}%`;
      }
    
    case 'currency':
      // Avoid showing "-$0" for tiny negative values
      {
        const numValue = Number(value);
        if (!Number.isFinite(numValue)) return '0';
        const sanitized = Math.abs(numValue) < 0.5 ? 0 : numValue;
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(sanitized);
      }
    
    case 'number':
      {
        const numValue = Number(value);
        if (!Number.isFinite(numValue)) return '0';
        return numValue.toLocaleString();
      }
    
    default:
      return String(value);
  }
};

/**
 * Determine trend direction for color coding metrics
 * @param {number} value - The metric value
 * @param {string} type - The type of metric ('roi', 'winRate', 'wins', 'places', 'races', 'tips')
 * @returns {'positive'|'negative'|'neutral'} Trend direction
 */
export const getMetricTrend = (value, type) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'neutral';
  }

  const numValue = Number(value);

  switch (type) {
    case 'roi':
      if (numValue > 0) return 'positive';
      if (numValue < 0) return 'negative';
      return 'neutral';
    
    case 'winRate':
      // Win rates above 20% are considered good for horse racing
      if (numValue >= 20) return 'positive';
      if (numValue >= 10) return 'neutral';
      return 'negative';
    
    case 'wins':
    case 'places':
    case 'races':
    case 'tips':
      // For count metrics, any positive value is good
      if (numValue > 0) return 'positive';
      return 'neutral';
    
    default:
      return 'neutral';
  }
};

/**
 * Get CSS classes for trend-based styling
 * @param {'positive'|'negative'|'neutral'} trend - The trend direction
 * @returns {string} CSS classes for styling
 */
export const getTrendClasses = (trend) => {
  switch (trend) {
    case 'positive':
      return 'bg-green-100 text-green-800';
    case 'negative':
      return 'bg-red-100 text-red-800';
    case 'neutral':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Calculate percentage change between two values
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {number} Percentage change
 */
export const calculatePercentageChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Get metric configuration for dashboard display
 * @returns {Array} Array of metric configurations
 */
export const getMetricConfigurations = () => [
  {
    key: 'totalROI',
    title: 'Overall ROI',
    icon: '📈',
    formatter: (value) => formatMetricValue(value, 'roi'),
    trendCalculator: (value) => getMetricTrend(value, 'roi'),
    description: 'Return on investment across all tips'
  },
  {
    key: 'totalWinRate',
    title: 'Win Rate',
    icon: '🎯',
    formatter: (value) => formatMetricValue(value, 'percentage'),
    trendCalculator: (value) => getMetricTrend(value, 'winRate'),
    description: 'Percentage of tips that resulted in wins'
  },
  {
    key: 'totalWins',
    title: 'Total Wins',
    icon: '🏆',
    formatter: (value) => formatMetricValue(value, 'number'),
    trendCalculator: (value) => getMetricTrend(value, 'wins'),
    description: 'Total number of winning tips'
  },
  {
    key: 'totalPlaces',
    title: 'Total Places',
    icon: '🥈',
    formatter: (value) => formatMetricValue(value, 'number'),
    trendCalculator: (value) => getMetricTrend(value, 'places'),
    description: 'Total number of placed tips'
  },
  {
    key: 'netReturns',
    title: 'Net Returns',
    icon: '💰',
    formatter: (value) => formatMetricValue(value, 'currency'),
    trendCalculator: (value) => getMetricTrend(value, 'roi'),
    description: 'Total profit/loss from all tips'
  },
  {
    key: 'totalTips',
    title: 'Total Tips',
    icon: '📊',
    formatter: (value) => formatMetricValue(value, 'number'),
    trendCalculator: (value) => getMetricTrend(value, 'tips'),
    description: 'Total number of tips placed'
  }
];

/**
 * Validate metric data structure
 * @param {Object} metrics - Metrics object to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateMetrics = (metrics) => {
  if (!metrics || typeof metrics !== 'object') {
    return false;
  }

  const requiredFields = [
    'totalROI', 'totalWinRate', 'totalWins', 'totalPlaces', 
    'totalRaces', 'totalTips', 'netReturns', 'averageROI'
  ];

  return requiredFields.every((field) => {
    if (!Object.prototype.hasOwnProperty.call(metrics, field)) return false;
    const value = metrics[field];
    if (value === null || value === undefined) return false;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num);
  });
};

/**
 * Determine data freshness and time period context
 * @param {Date} lastUpdated - When the data was last updated
 * @returns {Object} Time period context information
 */
export const getDataTimePeriodContext = (lastUpdated) => {
  if (!lastUpdated) {
    return {
      period: 'unknown',
      label: 'Overall Performance',
      description: 'System-wide metrics across all available data',
      freshness: 'unknown',
      isToday: false,
      isRecent: false
    };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const updateDate = new Date(lastUpdated);
  const updateDay = new Date(updateDate.getFullYear(), updateDate.getMonth(), updateDate.getDate());
  
  const daysDiff = Math.floor((today - updateDay) / (1000 * 60 * 60 * 24));
  const hoursDiff = Math.floor((now - updateDate) / (1000 * 60 * 60));
  const minutesDiff = Math.floor((now - updateDate) / (1000 * 60));

  // Today's data
  if (daysDiff === 0) {
    let freshness = 'current';
    if (minutesDiff < 5) freshness = 'live';
    else if (minutesDiff < 30) freshness = 'recent';
    else if (hoursDiff < 2) freshness = 'current';
    else freshness = 'stale';

    return {
      period: 'today',
      label: "Today's Performance",
      description: `Live racing data updated ${formatTimeAgo(updateDate)}`,
      freshness,
      isToday: true,
      isRecent: true,
      daysDiff: 0,
      hoursDiff,
      minutesDiff
    };
  }

  // Yesterday's data
  if (daysDiff === 1) {
    return {
      period: 'yesterday',
      label: "Yesterday's Performance",
      description: `Racing data from ${updateDate.toLocaleDateString('en-AU', { weekday: 'long' })}`,
      freshness: 'day-old',
      isToday: false,
      isRecent: true,
      daysDiff: 1,
      hoursDiff,
      minutesDiff
    };
  }

  // This week (2-7 days ago)
  if (daysDiff <= 7) {
    return {
      period: 'week',
      label: 'Recent Performance',
      description: `Racing data from ${daysDiff} days ago`,
      freshness: 'recent',
      isToday: false,
      isRecent: true,
      daysDiff,
      hoursDiff,
      minutesDiff
    };
  }

  // Older data
  return {
    period: 'historical',
    label: 'Historical Performance',
    description: `Racing data from ${updateDate.toLocaleDateString('en-AU')}`,
    freshness: 'historical',
    isToday: false,
    isRecent: false,
    daysDiff,
    hoursDiff,
    minutesDiff
  };
};

/**
 * Format time ago in a human-readable way
 * @param {Date} date - The date to format
 * @returns {string} Human-readable time ago string
 */
export const formatTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - new Date(date);
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  
  return new Date(date).toLocaleDateString('en-AU');
};

/**
 * Get appropriate messaging for no data scenarios based on time context
 * @returns {Object} No data messaging context
 */
export const getNoDataContext = () => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
  
  // Early morning (before 8 AM)
  if (currentHour < 8) {
    return {
      message: "Racing data is being prepared for today",
      subtitle: "Performance metrics will be available once racing begins",
      suggestion: "Check back after 8:00 AM for today's racing data"
    };
  }
  
  // Late evening (after 10 PM)
  if (currentHour >= 22) {
    return {
      message: "Today's racing has concluded",
      subtitle: "Performance data will be updated for tomorrow's races",
      suggestion: "Check back tomorrow for new racing data"
    };
  }
  
  // Weekend considerations (Saturday/Sunday)
  if (currentDay === 0 || currentDay === 6) {
    return {
      message: "Limited racing data available",
      subtitle: "Sunday racing schedules may vary",
      suggestion: "Check back during weekdays for regular racing data"
    };
  }
  
  // Default message
  return {
    message: "No performance data available at this time",
    subtitle: "Racing data may be updating or unavailable",
    suggestion: "Please check back later or refresh the page"
  };
};